import { useCallback } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import { getSourceValues } from '@/modules/estateWaterfall/EstateWaterfall.utils';

import { useEstateWaterfallHypotheticalGratModalContext } from '../EstateWaterfallHypotheticalGratModal.context';
import { HypotheticalGratFormShape } from '../EstateWaterfallHypotheticalGratModal.types';
import {
  getFundingIds,
  getGratOptions,
  getSourceEntityOptions,
  getSourceIndividualOptions,
  mapQueryDataToFormData,
} from '../EstateWaterfallHypotheticalGratModal.utils';
import { EstateWaterfallHypotheticalGratModalQuery } from '../graphql/EstateWaterfallHypotheticalGratModal.generated';

export function useSyncDataToForm() {
  const { getValues, reset } = useFormContext<HypotheticalGratFormShape>();
  const {
    waterfallId,
    gratId,
    initialSourceId,
    setGratOptions,
    setSourceEntityOptions,
    setSourceIndividualOptions,
    setSourceValues,
    setFundingId,
  } = useEstateWaterfallHypotheticalGratModalContext();

  const syncDataToForm = useCallback(
    (data: EstateWaterfallHypotheticalGratModalQuery) => {
      const gratOptions = getGratOptions(data, gratId ?? undefined);
      const sourceEntityOptions = getSourceEntityOptions(data);
      const sourceValues = getSourceValues(data);
      const sourceIndividualOptions = getSourceIndividualOptions(data);
      const fundingIds = getFundingIds(data);

      const hasInitialSourceId = sourceEntityOptions.some(
        (option) => option.value === initialSourceId
      );

      const formData = mapQueryDataToFormData({
        data,
        hypotheticalWaterfallId: waterfallId,
        initialSourceId: hasInitialSourceId ? initialSourceId : undefined,
        gratId: gratId ?? undefined,
        displayName: getValues('displayName'),
        sourceId: getValues('sourceId'),
        simulatedGratId: getValues('simulatedGratId'),
      });

      if (formData) {
        // Let the context update before resetting the form
        setTimeout(() => {
          reset(formData);
        });
      }

      setGratOptions(gratOptions);
      setSourceEntityOptions(sourceEntityOptions);
      setSourceValues(sourceValues);
      setSourceIndividualOptions(sourceIndividualOptions);
      setFundingId(fundingIds.find((f) => f.gratId === gratId)?.value ?? '');
    },
    [
      gratId,
      waterfallId,
      initialSourceId,
      getValues,
      setGratOptions,
      setSourceEntityOptions,
      setSourceValues,
      setSourceIndividualOptions,
      setFundingId,
      reset,
    ]
  );

  return { syncDataToForm };
}
